import React, { useState, useEffect } from 'react';
import { formatTimestamp, getInitial, formatGenericMessage, formatResponseMessageGenerically } from './ChatUtils';
import './ChatWindow.css';
import downloadFile from './DownloadFile';
import { useLoadingStatus, useDocumentProcessingStatus, useAudioLoadingStatus } from './useLoadingStatus';
import { exportAsEmailDraft, downloadAsEml, copyToClipboard } from './ExportUtils';
import { api } from '../api/robyBackend';
import { startLektoratJob, pollLektoratUntilDone, downloadLektoratResult } from './LektoratUtils';
import { LektoratReviewModal } from './LektoratReviewModal';

export const MessageList = ({ app, messages, messagesEndRef, sessionId, processingDocument, processingMessage, processingSuccess, checkAgents, onCheckMessage, checkingMessageId, lektoratExport }) => {
  const [fullscreenMessage, setFullscreenMessage] = useState(null);
  const [checkPopupMessageId, setCheckPopupMessageId] = useState(null);
  const [exportMenuMessageId, setExportMenuMessageId] = useState(null);
  const [exportMenuPosition, setExportMenuPosition] = useState(null);
  const [emailDraftLoading, setEmailDraftLoading] = useState(false);
  const [readyMailtoUrl, setReadyMailtoUrl] = useState(null);
  const [readyEmailContent, setReadyEmailContent] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  const [lektoratExporting, setLektoratExporting] = useState(false);
  const [showLektoratReview, setShowLektoratReview] = useState(false);

  useEffect(() => {
    if (!exportMenuMessageId) {
      setReadyMailtoUrl(null);
      setReadyEmailContent(null);
      setEmailDraftLoading(false);
    }
  }, [exportMenuMessageId]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get('/database/user');
        const { firstname = '', lastname = '' } = response.data || {};
        const fullName = [firstname, lastname].filter(Boolean).join(' ');
        setCurrentUserName(fullName);
      } catch (e) {
        // Kein Benutzername verfügbar – Fallback bleibt leer
      }
    };
    fetchUser();
  }, []);

  const openFullscreen = (messageContent) => {
    setFullscreenMessage(messageContent);
  };

  const closeFullscreen = () => {
    setFullscreenMessage(null);
  };

  // Dynamische Status-Nachrichten für Typing-Indicator
  // Unterscheide zwischen normalen Text-Nachrichten und Audio-Nachrichten
  const typingMessage = messages.find(m => m.type === 'typing');
  const hasTypingMessage = !!typingMessage;
  const isAudioTyping = hasTypingMessage && typingMessage.id && String(typingMessage.id).startsWith('audio-typing-');

  // Verwende den passenden Hook basierend auf dem Nachrichtentyp
  // Falls processingMessage vom Backend kommt (z.B. "Analysiere Seiten 21-40... (2/5)"), wird diese bevorzugt
  const { message: normalTypingMessage, isTransitioning: isNormalTransitioning } = useLoadingStatus(
    hasTypingMessage && !isAudioTyping,
    processingMessage  // 🔴 NEU: Backend-Message für Chunk-Progress
  );
  const { message: audioTypingMessage, isTransitioning: isAudioTransitioning } = useAudioLoadingStatus(hasTypingMessage && isAudioTyping);

  // Wähle die richtige Nachricht basierend auf dem Typ
  const typingStatusMessage = isAudioTyping ? audioTypingMessage : normalTypingMessage;
  const isTypingTransitioning = isAudioTyping ? isAudioTransitioning : isNormalTransitioning;

  // Dynamische Status-Nachrichten für Dokument-Verarbeitung
  const { message: documentStatusMessage, isTransitioning: isDocTransitioning } = useDocumentProcessingStatus(
    processingDocument && !processingSuccess,
    processingMessage
  );

  const toSingleLinePlainText = (value) => {
    if (typeof value !== 'string') return '';

    return value
      // Treat common HTML separators as spaces
      .replace(/<\s*br\s*\/?>/gi, ' ')
      .replace(/<\s*\/\s*div\s*>/gi, ' ')
      .replace(/<\s*\/\s*p\s*>/gi, ' ')
      // Strip all remaining tags
      .replace(/<[^>]*>/g, ' ')
      // Strip attention emojis
      .replace(/(⚠️|❌)\s*/gu, '')
      // Collapse whitespace
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  const sanitizeStatusHtmlForDisplay = (html) => {
    if (typeof html !== 'string') return '';

    return html
      // Remove inline styles so we don't get multiple text colors
      .replace(/\sstyle\s*=\s*(['"]).*?\1/gi, '')
      // Remove attention emojis everywhere (avoid duplicates)
      .replace(/(⚠️|❌)\s*/gu, '')
      .trim();
  };

  const renderUploadFailureHtml = (html, { addWarningIcon } = {}) => {
    let sanitized = sanitizeStatusHtmlForDisplay(html);
    if (addWarningIcon) {
      // Add exactly one attention icon back in front of the known headline.
      sanitized = sanitized.replace(
        /Dokument-Upload fehlgeschlagen/,
        '⚠️ Dokument-Upload fehlgeschlagen'
      );
    }
    if (!sanitized) return null;
    return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
  };

  const handleCheckClick = (msg) => {
    if (!checkAgents || checkAgents.length === 0 || !onCheckMessage) return;

    // Rohtext extrahieren (HTML-Tags entfernen)
    const rawText = typeof msg.text === 'string'
      ? msg.text.replace(/<[^>]*>/g, '').trim()
      : '';

    if (checkAgents.length === 1) {
      onCheckMessage(rawText, checkAgents[0].agent_id || checkAgents[0].name);
    } else {
      setCheckPopupMessageId(msg.id);
    }
  };

  const handleAgentSelect = (msg, agent) => {
    const rawText = typeof msg.text === 'string'
      ? msg.text.replace(/<[^>]*>/g, '').trim()
      : '';
    onCheckMessage(rawText, agent.agent_id || agent.name);
    setCheckPopupMessageId(null);
  };

  const handleExportClick = (msg, e) => {
    if (exportMenuMessageId === msg.id) {
      setExportMenuMessageId(null);
      setExportMenuPosition(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setExportMenuPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left,
      });
      setExportMenuMessageId(msg.id);
    }
  };

  const handleExportAsEmailDraft = async (msg) => {
    setEmailDraftLoading(true);
    setReadyMailtoUrl(null);
    try {
      const result = await exportAsEmailDraft(msg.text, msg.subject || 'Anfrage Robyflow', currentUserName);
      if (result) {
        setReadyMailtoUrl(result.mailtoUrl);
        setReadyEmailContent({ subject: result.subject, content: result.content });
      } else {
        // Fallback (Zwischenablage) wurde ausgelöst – Menü schließen
        setExportMenuMessageId(null);
        setExportMenuPosition(null);
      }
    } catch (error) {
      console.error('Fehler beim Email-Export:', error);
      alert('Fehler beim Exportieren als Email-Entwurf');
      setExportMenuMessageId(null);
      setExportMenuPosition(null);
    } finally {
      setEmailDraftLoading(false);
    }
  };

  const handleCopyToClipboard = (msg) => {
    try {
      copyToClipboard(msg.text);
      setExportMenuMessageId(null);
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
      alert('Fehler beim Kopieren in die Zwischenablage');
    }
  };

  const triggerLektoratDownload = async () => {
    setLektoratExporting(true);
    try {
      await startLektoratJob(app, sessionId);
      await pollLektoratUntilDone(app, sessionId, {});
      await downloadLektoratResult(app, sessionId);
    } catch (err) {
      if (err.response?.status === 404) {
        // Noch keine .docx für diese Session hochgeladen → derselbe Upload-Button/Popup wie bei anderen Dokumenten
        alert('Es ist noch kein Word-Dokument für diese Session hochgeladen. Bitte über den Upload-Button im Chat-Eingabefeld eine .docx-Datei hochladen und danach erneut exportieren.');
      } else {
        const detail = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
        alert('Fehler beim Word-Export: ' + detail);
      }
    } finally {
      setLektoratExporting(false);
    }
  };

  const handleLektoratExport = () => {
    setExportMenuMessageId(null);
    setExportMenuPosition(null);
    triggerLektoratDownload();
  };

  const handleLektoratInteractive = () => {
    setExportMenuMessageId(null);
    setExportMenuPosition(null);
    setShowLektoratReview(true);
  };

  const handleCitationLinkClick = async (e, entry, document) => {
    e.preventDefault();
    
    try {
      // Extrahiere den Blob-Pfad aus der URL (alles vor dem `?`)
      const currentUrl = entry.url;
      const urlParts = currentUrl.split('?')[0]; // Entferne Query-Parameter
      const blobPath = urlParts.split('/').pop() === '' 
        ? urlParts.substring(0, urlParts.length - 1)
        : urlParts;

      // Extrahiere nur den relativen Pfad (nach /container/)
      const containerRegex = /\.blob\.core\.windows\.net\/[^/]+\/(.+)$/;
      const match = urlParts.match(containerRegex);
      const relativeBlobPath = match ? match[1] : blobPath;

      console.log('Generiere neuen SAS-Token für:', relativeBlobPath);

      // Rufe den Backend-Endpoint auf, um einen neuen SAS-Token zu generieren
      const response = await api.post('/chat/get-sas-url', { blobPath: relativeBlobPath });

      if (response.data && response.data.url) {
        const newUrl = response.data.url;
        const pageNumber = entry.page;

        // Für PDFs: Seite im Fragment anhängen
        const isPdf = document.toLowerCase().endsWith('.pdf');
        const finalUrl = isPdf && pageNumber 
          ? `${newUrl}#page=${pageNumber}`
          : newUrl;

        // Öffne das Dokument in einem neuen Tab
        window.open(finalUrl, '_blank');
      } else {
        throw new Error('Keine URL in der Antwort erhalten');
      }
    } catch (error) {
      console.error('Fehler beim Generieren des SAS-Links:', error);
      // Fallback: Versuche, die alte URL direkt zu öffnen (falls noch gültig)
      try {
        window.open(entry.url, '_blank');
      } catch (fallbackError) {
        alert('Fehler beim Öffnen des Dokuments. Der Link ist möglicherweise abgelaufen.');
      }
    }
  };


  const renderMessage = (msg, idx) => {
    const key = msg.id || `${msg.timestamp}-${idx}`;

    // Typing Indicator (Bot, User or Check) - mit dynamischen Status-Nachrichten
    if (msg.type === 'typing') {
      const isUserTyping = msg.user.toLowerCase() === 'user';
      const isCheckTyping = msg.user.toLowerCase() === 'check';

      if (isCheckTyping) {
        return (
          <div key={key} className="bot-message-container check-message-container typing-container">
            <div className="check-avatar">&#x2713;</div>
            <div className="typing-indicator-enhanced">
              <div className="typing-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
              <span className="typing-status-text">
                Nachricht wird geprüft<span className="animated-dots"></span>
              </span>
            </div>
          </div>
        );
      }

      const containerClass = `${isUserTyping ? 'user-message-container' : 'bot-message-container'} typing-container`;
      const avatarClass = isUserTyping ? 'user-avatar' : 'bot-avatar';
      const initial = isUserTyping ? 'U' : 'R';
      return (
        <div key={key} className={containerClass}>
          <div className={avatarClass}>{getInitial(initial)}</div>
          <div className="typing-indicator-enhanced">
            <div className="typing-dots">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
            <span className={`typing-status-text${isTypingTransitioning ? ' transitioning' : ''}`}>
              {typingStatusMessage}<span className="animated-dots"></span>
            </span>
          </div>
        </div>
      );
    }

    // Use correct timestamp property
    const formattedTimestamp = formatTimestamp(msg.timestamp, msg.totalTokens);

    // Check Messages - eigenes Styling
    if (msg.type === 'check') {
      const messageContent = formatGenericMessage(msg.text);
      return (
        <div key={key} className="bot-message-container check-message-container">
          <div className="check-avatar">&#x2713;</div>
          <div className={`bot-message check-message${msg.status === 'warning' ? ' check-message--warning' : ''}`}>
            {msg.checkedBy && (
              <div className="check-header">Geprüft von: {msg.checkedBy}</div>
            )}
            <div dangerouslySetInnerHTML={{ __html: messageContent }} />
            <button
              className="fullscreen-button"
              onClick={() => openFullscreen(messageContent)}
              title="Vollbild anzeigen"
            >
              ⛶
            </button>
            <div className="timestamp">{formattedTimestamp}</div>
          </div>
        </div>
      );
    }

    // Bot Messages
    if (msg.user.toLowerCase() === 'bot') {
      const rawText = typeof msg.text === 'string' ? msg.text : '';
      const lowerText = rawText.toLowerCase();
      const isUploadFailureText =
        lowerText.includes('hochlad') &&
        (lowerText.includes('nicht erfolgreich') ||
          lowerText.includes('fehlgeschlagen') ||
          lowerText.includes('konnte') ||
          lowerText.includes('fehler'));

      // Warning (yellow) for document upload failures / warnings
      const isWarningMessage =
        msg.type === 'warning' ||
        (rawText && rawText.includes('⚠️')) ||
        isUploadFailureText;

      // Error (red) for hard errors (unless it is classified as warning above)
      const isErrorMessage =
        !isWarningMessage &&
        (msg.type === 'error' || (rawText && rawText.includes('❌')));

      if (isWarningMessage) {
        return (
          <div key={key} className="bot-message-container processing-container">
            <div className="bot-avatar">{getInitial('R')}</div>
            <div className="bot-message processing-message warning">
              <div className="processing-text">{renderUploadFailureHtml(msg.text, { addWarningIcon: true })}</div>
              <div className="timestamp">{formattedTimestamp}</div>
            </div>
          </div>
        );
      }

      if (isErrorMessage) {
        return (
          <div key={key} className="bot-message-container processing-container">
            <div className="bot-avatar">{getInitial('R')}</div>
            <div className="bot-message processing-message error">
              <div className="processing-text">{renderUploadFailureHtml(msg.text)}</div>
              <div className="timestamp">{formattedTimestamp}</div>
            </div>
          </div>
        );
      }

      // Check if this is a success message (type === "success" or contains success text)
      const isSuccessMessage = msg.type === 'success' ||
                              (msg.text && (msg.text.includes('erfolgreich verarbeitet') || msg.text.includes('✅')));

      if (isSuccessMessage) {
        const containerClass = `bot-message-container processing-container${msg.fadingOut ? ' fading-out' : ''}`;
        return (
          <div key={key} className={containerClass}>
            <div className="bot-avatar">{getInitial('R')}</div>
            <div className="bot-message processing-message success">
              <div className="processing-indicator">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
              <div className="processing-text" dangerouslySetInnerHTML={{ __html: msg.text }} />
              <div className="timestamp">{formattedTimestamp}</div>
            </div>
          </div>
        );
      }

      const messageContent = msg.isJsonResponse && msg.jsonData
        ? formatResponseMessageGenerically(msg.jsonData)
        : formatGenericMessage(msg.text);

      const isPopupOpen = exportMenuMessageId === msg.id || checkPopupMessageId === msg.id;
      return (
        <div key={key} className="bot-message-container">
          <div className="bot-avatar">{getInitial('R')}</div>
          <div className={`bot-message${isPopupOpen ? ' bot-message--popup-open' : ''}`}>
            <div dangerouslySetInnerHTML={{ __html: messageContent }} />
            <button
              className="fullscreen-button"
              onClick={() => openFullscreen(messageContent)}
              title="Vollbild anzeigen"
            >
              ⛶
            </button>
            <div className="message-action-buttons">
              <button
                className="export-button"
                onClick={(e) => handleExportClick(msg, e)}
                title="Nachricht exportieren"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Exportieren
              </button>
              {checkAgents && checkAgents.length > 0 && msg.type === 'msg' && (
                <button
                  className="check-message-button"
                  onClick={() => handleCheckClick(msg)}
                  title="Nachricht prüfen"
                  disabled={!!checkingMessageId}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  </svg>
                  Prüfen
                </button>
              )}
            </div>
            {checkPopupMessageId === msg.id && (
              <div className="check-agent-popup">
                <div className="check-agent-popup-title">Prüfagent wählen</div>
                {checkAgents.map((agent, agentIdx) => (
                  <button
                    key={agentIdx}
                    className="check-agent-option"
                    onClick={() => handleAgentSelect(msg, agent)}
                  >
                    <span className="check-agent-option-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                      </svg>
                    </span>
                    <span className="check-agent-option-text">
                      <span className="check-agent-option-name">{agent.name}</span>
                      {agent.description && (
                        <span className="check-agent-option-desc">{agent.description}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {msg.isJsonResponse && msg.type === 'msg' && (
              <div className="download-button-container">
                <button className="download-button" onClick={() => downloadFile(app, sessionId)}>
                  Report herunterladen
                </button>
              </div>
            )}
            {msg.isCitationResponse && Array.isArray(msg.citations) && (
              <div className="citations-grouped">
                {/* Dokument-Zitate */}
                {Object.entries(
                  msg.citations.reduce((acc, cite) => {
                    const isWeb = cite.type === 'web' || (cite.url && cite.url.startsWith('http') && !cite.page);
                    if (isWeb) return acc;
                    const { document, page, url } = cite;
                    if (!document || !page || !url) return acc;
                    if (!acc[document]) acc[document] = [];
                    acc[document].push({ page, url });
                    return acc;
                  }, {})
                )
                  .sort(([docA], [docB]) => docA.localeCompare(docB))
                  .map(([document, entries], docIndex) => {
                    const sortedPages = entries.sort((a, b) => parseInt(a.page, 10) - parseInt(b.page, 10));

                    return (
                      <div key={docIndex} className="citation-document-group">
                        <span className="citation-label">[{docIndex + 1}]</span>
                        <span className="citation-doc-name">{document} – </span>
                        {sortedPages.map((entry, i) => {
                          const isMissing = entry.url === "no document available";
                          const isPdf = document.toLowerCase().endsWith('.pdf');
                          const isWord = document.toLowerCase().endsWith(('.docx')) || document.toLowerCase().endsWith('.doc');
                          return (
                            <span key={i} className="citation-page-wrapper">
                              {i > 0 && ', '}
                              {isMissing ? (
                                <>
                                  <span className="citation-page-wrapper citation-missing">
                                    {entry.page}
                                  </span>
                                  <span className="citation-tooltip">No file available</span>
                                </>
                              ) : isPdf ? (
                                <button
                                  type="button"
                                  onClick={(e) => handleCitationLinkClick(e, entry, document)}
                                  className="citation-page-link"
                                >
                                  {entry.page}
                                </button>
                              ) : isWord ? (
                                <button
                                  type="button"
                                  onClick={(e) => handleCitationLinkClick(e, entry, document)}
                                  className="citation-page-link"
                                  title="Word-Dokument als HTML anzeigen"
                                >
                                  {entry.page}
                                </button>
                              ) : (
                                <span className="citation-page-wrapper">
                                  {entry.page}
                                </span>
                              )}
                              {!isMissing && isPdf && <span className="citation-tooltip">Go to document & page</span>}
                              {!isMissing && isWord && <span className="citation-tooltip">Dokument anzeigen</span>}
                            </span>
                          );
                        })}
                      </div>
                    );
                  })}
                {/* Web-Zitate */}
                {msg.citations.filter(c =>
                  c.type === 'web' || (c.url && c.url.startsWith('http') && !c.page)
                ).map((webCite, i) => (
                  <div key={`web-${i}`} className="citation-document-group citation-web-group">
                    <span className="citation-label">[W]</span>
                    <a
                      href={webCite.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="citation-web-link"
                      title={webCite.extract || webCite.url}
                    >
                      {webCite.title && webCite.title !== webCite.url ? webCite.title : webCite.url}
                    </a>
                  </div>
                ))}
              </div>
            )}
            <div className="timestamp">{formattedTimestamp}</div>
          </div>
        </div>
      );
    }

    // User Messages (including audio)
    if (msg.user.toLowerCase() === 'user') {
      // Audio Messages
      if (msg.type === 'audio' && msg.blobPath) {
        // Verwende direkt die Azure Blob Storage URL mit SAS Token
        // Format: https://blobdevrobyflowdev.blob.core.windows.net/robyflow-container/reportsaudio/recording_xxx.wav
        const audioUrl = msg.audioUrl || `/chat/download/?folder=${encodeURIComponent(msg.blobPath.split('/')[0])}&filename=${encodeURIComponent(msg.blobPath.split('/')[1])}`;

        const handleError = (e) => {
          console.error('Audio Fehler:', {
            error: e.target.error,
            errorCode: e.target.error?.code,
            errorMessage: e.target.error?.message,
            networkState: e.target.networkState,
            readyState: e.target.readyState,
            url: audioUrl,
            blobPath: msg.blobPath
          });
        };

        const handleLoadedMetadata = (e) => {
          console.log('✅ Audio erfolgreich geladen:', audioUrl, 'Dauer:', e.target.duration, 's');
        };

        const handleCanPlay = () => {
          console.log('✅ Audio kann abgespielt werden:', audioUrl);
        };

        return (
          <div key={key} className="user-message-container">
            <div className="user-avatar">{getInitial('U')}</div>
            <div className="user-message audio-message">
              <div className="audio-content">
                <span className="audio-icon">🎤</span>
                <div className="audio-player-wrapper">
                  <span className="audio-label">Sprachnachricht</span>
                  <audio
                    controls
                    className="audio-player"
                    src={audioUrl}
                    preload="metadata"
                    onError={handleError}
                    onLoadedMetadata={handleLoadedMetadata}
                    onCanPlay={handleCanPlay}
                  >
                    Ihr Browser unterstützt keine Audio-Wiedergabe.
                  </audio>
                </div>
              </div>
              <div className="timestamp">{formattedTimestamp}</div>
            </div>
          </div>
        );
      }

      // Regular User Messages
      return (
        <div key={key} className="user-message-container">
          <div className="user-avatar">{getInitial('U')}</div>
          <div className="user-message">
            <div dangerouslySetInnerHTML={{ __html: formatGenericMessage(msg.text) }} />
            <div className="timestamp">{formattedTimestamp}</div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Zeige Processing-Message wenn Processing läuft (aber nicht bei Success, da Success in History ist)
  // Nicht anzeigen wenn Typing-Indicator aktiv ist - der zeigt den Fortschritt bereits
  const shouldShowProcessing = processingDocument && processingMessage && !processingSuccess && !hasTypingMessage;

  const processingLower = (processingMessage || '').toLowerCase();
  const isProcessingWarning =
    processingLower.includes('hochlad') &&
    (processingLower.includes('nicht erfolgreich') ||
      processingLower.includes('fehlgeschlagen') ||
      processingLower.includes('konnte') ||
      processingLower.includes('fehler'));

  console.log('🎬 MessageList render:', {
    processingDocument,
    processingMessage,
    processingSuccess,
    shouldShowProcessing,
    messagesCount: messages.length
  });

  return (
    <div className="messages-container">
      {messages.map((msg, idx) => renderMessage(msg, idx))}
      {shouldShowProcessing && (
        <div className="bot-message-container processing-container">
          <div className="bot-avatar">{getInitial('R')}</div>
          <div className={`bot-message processing-message${isProcessingWarning ? ' warning' : ''}`}>
            <div className="processing-indicator">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
            <div className={`processing-text${isDocTransitioning ? ' transitioning' : ''}`}>
              {documentStatusMessage}<span className="animated-dots"></span>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />

      {/* Check Agent Popup Overlay */}
      {checkPopupMessageId && (
        <div className="check-agent-popup-overlay" onClick={() => setCheckPopupMessageId(null)} />
      )}

      {/* Export Menu Overlay – schließt bei Klick außerhalb */}
      {exportMenuMessageId && (
        <div className="check-agent-popup-overlay" onClick={() => { setExportMenuMessageId(null); setExportMenuPosition(null); }} />
      )}

      {/* Export Menu Popup – position: fixed, außerhalb des Scroll-Containers */}
      {exportMenuMessageId && exportMenuPosition && (
        <div
          className="export-menu-popup"
          style={{
            position: 'fixed',
            bottom: exportMenuPosition.bottom,
            left: exportMenuPosition.left,
            top: 'auto',
          }}
        >
          <div className="check-agent-popup-title">Exportieren als</div>
          {lektoratExport && (
            <button
              className="check-agent-option"
              onClick={handleLektoratExport}
              disabled={lektoratExporting}
              style={{ opacity: lektoratExporting ? 0.6 : 1 }}
            >
              <span className="check-agent-option-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="9" y1="13" x2="15" y2="13"/>
                  <line x1="9" y1="17" x2="13" y2="17"/>
                </svg>
              </span>
              <span className="check-agent-option-text">
                <span className="check-agent-option-name">
                  {lektoratExporting ? 'Wird verarbeitet…' : 'Word (Track Changes)'}
                </span>
                <span className="check-agent-option-desc" style={{ fontSize: '11px', color: '#888', display: 'block' }}>
                  Vollständiges Dokument mit Änderungsmarkierungen
                </span>
              </span>
            </button>
          )}
          {lektoratExport && (
            <button
              className="check-agent-option"
              onClick={handleLektoratInteractive}
            >
              <span className="check-agent-option-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                </svg>
              </span>
              <span className="check-agent-option-text">
                <span className="check-agent-option-name">Interaktiv prüfen</span>
                <span className="check-agent-option-desc" style={{ fontSize: '11px', color: '#888', display: 'block' }}>
                  Änderungen im Chat annehmen oder ablehnen
                </span>
              </span>
            </button>
          )}
          {emailDraftLoading ? (
            <button className="check-agent-option" disabled style={{ opacity: 0.6 }}>
              <span className="check-agent-option-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <span className="check-agent-option-text">
                <span className="check-agent-option-name">Wird vorbereitet…</span>
              </span>
            </button>
          ) : readyMailtoUrl ? (
            <a
              className="check-agent-option"
              href={readyMailtoUrl}
              style={{ textDecoration: 'none' }}
              onClick={() => {
                // Blur-Erkennung: öffnet kein Mail-Client (z.B. TS-Plus blockiert mailto:),
                // verliert das Fenster keinen Fokus → .eml-Fallback nach 900ms
                const emailContent = readyEmailContent;
                let opened = false;
                const onBlur = () => { opened = true; };
                window.addEventListener('blur', onBlur, { once: true });
                setTimeout(() => {
                  window.removeEventListener('blur', onBlur);
                  if (!opened && emailContent) {
                    downloadAsEml(emailContent.subject, emailContent.content);
                  }
                }, 900);

                setReadyMailtoUrl(null);
                setReadyEmailContent(null);
                setExportMenuMessageId(null);
                setExportMenuPosition(null);
              }}
            >
              <span className="check-agent-option-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <span className="check-agent-option-text">
                <span className="check-agent-option-name">E-Mail in Mailprogramm öffnen ↗</span>
              </span>
            </a>
          ) : (
            <button
              className="check-agent-option"
              onClick={() => {
                const msg = messages.find(m => m.id === exportMenuMessageId);
                if (msg) handleExportAsEmailDraft(msg);
              }}
            >
              <span className="check-agent-option-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <span className="check-agent-option-text">
                <span className="check-agent-option-name">Email-Entwurf</span>
              </span>
            </button>
          )}
          <button
            className="check-agent-option"
            onClick={() => {
              const msg = messages.find(m => m.id === exportMenuMessageId);
              if (msg) handleCopyToClipboard(msg);
            }}
          >
            <span className="check-agent-option-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </span>
            <span className="check-agent-option-text">
              <span className="check-agent-option-name">In Zwischenablage kopieren</span>
            </span>
          </button>
        </div>
      )}

      <LektoratReviewModal
        isOpen={showLektoratReview}
        onClose={() => setShowLektoratReview(false)}
        app={app}
        sessionId={sessionId}
      />

      {/* Fullscreen Modal */}
      {fullscreenMessage && (
        <div className="fullscreen-overlay" onClick={closeFullscreen}>
          <div className="fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <button className="fullscreen-close-button" onClick={closeFullscreen}>
              ✕
            </button>
            <div
              className="fullscreen-content"
              dangerouslySetInnerHTML={{ __html: fullscreenMessage }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
